import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { idempotent, type QueryOpts } from '../internal/query-utils';
import {
  notificationsControllerListV1,
  notificationsControllerMarkAllReadV1,
  notificationsControllerMarkReadV1,
  notificationsControllerUnreadCountV1,
} from '../generated/endpoints';
import type { NotificationListResponse, UnreadCountResponse } from '../generated/models';

/**
 * Notification-center data layer (ADR-0135 §8) — typed hooks over the generated client, in the same
 * shape as the family/admin layers. The bell badge (`useUnreadCount`) refetches on focus and on a modest
 * interval WHILE the tab is visible (no websockets — consistent with the app's polling posture); the
 * center list is keyset-paginated (`useNotifications`). Mark-read mutations are optimistic (the badge
 * drops instantly) and carry a STABLE `Idempotency-Key` so the PWA offline outbox can replay them without
 * double-applying — the id for a single read, the `before` timestamp for a bulk read.
 */

const UNREAD_POLL_MS = 60_000;

export const notificationKeys = {
  all: () => ['notifications'] as const,
  list: () => ['notifications', 'list'] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
};

/**
 * A keyset page of the caller's notification history, newest first (`GET /v1/notifications`). Pass the
 * previous page's `nextCursor` to fetch older rows; each page caches under its own key slot (like the
 * ledger/quest history). `unreadOnly` narrows to unread rows.
 */
export function useNotifications(
  cursor: string | null = null,
  unreadOnly = false,
  options?: QueryOpts<NotificationListResponse>,
): UseQueryResult<NotificationListResponse, Error> {
  return useQuery({
    queryKey: [...notificationKeys.list(), unreadOnly ? 'unread' : 'all', cursor ?? 'first'],
    queryFn: ({ signal }) =>
      notificationsControllerListV1(
        { ...(cursor ? { cursor } : {}), ...(unreadOnly ? { unreadOnly: true } : {}) },
        undefined,
        signal,
      ),
    ...options,
  });
}

/** Infinite-query options a caller may tune (queryKey/queryFn/paging owned by the hook). */
type NotificationsInfiniteOpts = Omit<
  UseInfiniteQueryOptions<
    NotificationListResponse,
    Error,
    InfiniteData<NotificationListResponse>,
    readonly unknown[],
    string | null
  >,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;

/**
 * The notification center's paginated history (`GET /v1/notifications`) as an infinite query — each
 * "show older" appends the next keyset page (`nextCursor`), and mark-read invalidations (keyed on the
 * `list()` prefix) refresh every loaded page at once. `unreadOnly` narrows to unread rows.
 */
export function useNotificationsInfinite(
  unreadOnly = false,
  options?: NotificationsInfiniteOpts,
): UseInfiniteQueryResult<InfiniteData<NotificationListResponse>, Error> {
  return useInfiniteQuery({
    queryKey: [...notificationKeys.list(), unreadOnly ? 'unread' : 'all', 'infinite'],
    queryFn: ({ pageParam, signal }) =>
      notificationsControllerListV1(
        {
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(unreadOnly ? { unreadOnly: true } : {}),
        },
        undefined,
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    ...options,
  });
}

/**
 * The caller's unread count for the bell badge (`GET /v1/notifications/unread-count`). Refetches on
 * window focus and every ~60s WHILE the tab is visible (the interval pauses in a hidden tab so a
 * backgrounded PWA doesn't poll). A push arriving with the app open can also invalidate this key.
 */
export function useUnreadCount(
  options?: QueryOpts<UnreadCountResponse>,
): UseQueryResult<UnreadCountResponse, Error> {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: ({ signal }) => notificationsControllerUnreadCountV1(undefined, signal),
    refetchOnWindowFocus: true,
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'visible'
        ? UNREAD_POLL_MS
        : false,
    ...options,
  });
}

export interface MarkReadVars {
  readonly id: string;
}

/** Optimistic snapshot carried from onMutate → onError so a failed write rolls the badge back. */
interface UnreadSnapshot {
  readonly previousCount?: number;
}

/**
 * Mark one notification read (`POST /v1/notifications/:id/read`, idempotent). Optimistically decrements
 * the unread badge; the stable key (the notification id) makes an offline replay a no-op. Invalidates the
 * list + count on settle so the server reconciles the row's `readAt`. Callers pass any per-call callbacks
 * to `.mutate(vars, { onSuccess })` — the hook owns the optimistic lifecycle.
 */
export function useMarkRead(): UseMutationResult<void, Error, MarkReadVars, UnreadSnapshot> {
  const qc = useQueryClient();
  return useMutation<void, Error, MarkReadVars, UnreadSnapshot>({
    mutationFn: ({ id }) => notificationsControllerMarkReadV1(id, idempotent(`notif-read-${id}`)),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });
      const previous = qc.getQueryData<UnreadCountResponse>(notificationKeys.unreadCount());
      if (previous !== undefined) {
        qc.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
          count: Math.max(0, previous.count - 1),
        });
      }
      return { previousCount: previous?.count };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousCount !== undefined) {
        qc.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
          count: ctx.previousCount,
        });
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

export interface MarkAllReadVars {
  /** The newest `createdAt` the client has loaded — bounds the sweep so it can't race newer rows. */
  readonly before: string;
}

/**
 * Mark every unread notification created at/before `before` read (`POST /v1/notifications/read-all`).
 * Optimistically zeroes the badge; the stable key (the `before` timestamp) makes an offline replay safe.
 * Invalidates the list + count on settle.
 */
export function useMarkAllRead(): UseMutationResult<void, Error, MarkAllReadVars, UnreadSnapshot> {
  const qc = useQueryClient();
  return useMutation<void, Error, MarkAllReadVars, UnreadSnapshot>({
    mutationFn: ({ before }) =>
      notificationsControllerMarkAllReadV1({ before }, idempotent(`notif-readall-${before}`)),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });
      const previous = qc.getQueryData<UnreadCountResponse>(notificationKeys.unreadCount());
      qc.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), { count: 0 });
      return { previousCount: previous?.count };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousCount !== undefined) {
        qc.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
          count: ctx.previousCount,
        });
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}
